import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Wand2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Community() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [sort, setSort] = useState("recent");

  useEffect(() => { api.get(`/community?sort=${sort}`).then((r) => setItems(r.data)); }, [sort, user]);

  const like = async (g) => {
    if (!user) return nav("/signup");
    const { data } = await api.post(`/community/${g.generation_id}/like`);
    setItems(items.map((x) => (x.generation_id === g.generation_id ? { ...x, ...data } : x)));
    if (data.liked) toast.success("Liked!");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="community-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Community Feed</h1>
          <p className="mt-2 text-[#6E687A]">Fresh glow-ups from pet parents everywhere.</p>
        </div>
        <div className="inline-flex rounded-full bg-white border border-[#EAE3D9] p-1">
          {["recent", "top"].map((s) => (
            <button key={s} onClick={() => setSort(s)} data-testid={`community-sort-${s}`}
              className={`px-5 py-1.5 rounded-full text-sm font-bold capitalize transition-colors ${sort === s ? "bg-[#1E1B26] text-white" : ""}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="mt-10 columns-2 md:columns-3 lg:columns-4 gap-5 [&>*]:mb-5">
        {items === null && [0, 1, 2, 3].map((i) => <div key={i} className="shimmer h-64 rounded-3xl break-inside-avoid" />)}
        {items?.length === 0 && (
          <div className="rounded-3xl border-2 border-dashed border-[#EAE3D9] p-10 text-center break-inside-avoid" data-testid="community-empty">
            <p className="font-display text-xl font-semibold">Be the first to share!</p>
            <Link to={user ? "/studio" : "/signup"} className="mt-3 inline-block font-bold text-[#FF5A79]" data-testid="community-empty-cta">Create a masterpiece</Link>
          </div>
        )}
        {items?.map((g) => (
          <figure key={g.generation_id} className="break-inside-avoid rounded-3xl overflow-hidden bg-white border border-[#EAE3D9] group" data-testid={`community-item-${g.generation_id}`}>
            <Link to={`/c/${g.generation_id}`} data-testid={`community-open-${g.generation_id}`}><img src={fileUrl(g.output_path)} alt={g.prompt_title} className="w-full object-cover" /></Link>
            <figcaption className="p-4">
              <p className="font-display font-semibold">{g.pet_name} <span className="text-[#6E687A] font-normal">as</span> {g.prompt_title}</p>
              <p className="text-xs text-[#6E687A]">by {g.author}</p>
              <div className="mt-3 flex items-center justify-between">
                <button onClick={() => like(g)} data-testid={`community-like-${g.generation_id}`} className="flex items-center gap-1.5 text-sm font-bold">
                  <Heart className={`h-5 w-5 transition-transform active:scale-125 ${g.liked ? "fill-[#FF5A79] text-[#FF5A79]" : ""}`} /> {g.likes}
                </button>
                <button onClick={() => nav(user ? `/studio?prompt=${g.prompt_id}` : "/signup")} data-testid={`community-try-${g.generation_id}`}
                  className="flex items-center gap-1 rounded-full bg-[#F3EFFF] text-[#8A4FFF] px-3 py-1 text-xs font-bold hover:bg-[#8A4FFF] hover:text-white transition-colors">
                  <Wand2 className="h-3.5 w-3.5" /> Try this
                </button>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </main>
  );
}
